import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box,
  IconButton, Drawer, List, ListItemButton, ListItemText,
  useMediaQuery, useTheme, Chip, Avatar, Menu, MenuItem, Divider,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { CAREER_MODES, CAREER_MODE_META } from '../utils/careerModes';

// Phase C.1a — Fantasy/NFL/College/Tools/Pricing removed from the
// PRIMARY navigation: all five are the old NFL-analytics/fantasy-
// subscription identity (Pricing is exclusively the Nerds+ NFL-stats
// tier — see PricingPage.js). Their routes are deliberately left
// completely intact in App.js — this is a navigation change only, not a
// route removal, per this phase's explicit "do not break legacy routes
// merely for cleanup" instruction. My Athletes/My Teams/My Programs are
// the real, current FFN platform surfaces and are unchanged.
const NAV_ITEMS = [
  { label: '🏋️ My Athletes', path: '/athletes'      },
  { label: '📋 My Teams',    path: '/my-teams'       },
  { label: '🏟️ My Programs', path: '/my-programs'    },
];

// Discord icon (simple SVG)
function InstagramIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );
}

function DiscordIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

function UserMenu({ user, isPremium, isFree, isAdmin }) {
  const [anchor, setAnchor] = useState(null);
  const navigate = useNavigate();
  const { setUpgradeModalOpen, setAuthModalOpen, careerModes, activeCareerMode, setActiveMode } = useAuth();

  const handleSignOut = async () => {
    setAnchor(null);
    try {
      await Promise.race([
        supabase.auth.signOut({ scope: 'global' }),
        new Promise(r => setTimeout(r, 3000)), // give up after 3s
      ]);
    } catch (_) {}
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/');
  };
  const initial = user.displayName?.[0] || user.email?.[0] || '?';

  return (
    <>
      <Box onClick={e => setAnchor(e.currentTarget)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', ml: 1 }}>
        {user.photoURL
          ? <Avatar src={user.photoURL} sx={{ width: 32, height: 32, border: '2px solid #2ea84b' }} />
          : <Avatar sx={{ width: 32, height: 32, bgcolor: '#2ea84b', fontSize: '0.85rem', fontWeight: 700 }}>
              {initial.toUpperCase()}
            </Avatar>
        }
        {isAdmin && <Chip label="Admin" size="small"
          sx={{ fontSize: '0.58rem', height: 16, bgcolor: '#3a1a6e', color: '#c084fc', fontWeight: 800 }} />}
        {!isAdmin && isPremium && <Chip label="Nerds+" size="small" color="secondary"
          sx={{ fontSize: '0.6rem', height: 18, fontWeight: 800 }} />}
      </Box>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { backgroundColor: 'rgba(10,20,34,0.97)', border: '1px solid #4a7096', borderRadius: 2, minWidth: 220, mt: 1 } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <Box sx={{ px: 2, py: 1.5 }}>
          {user.displayName && <Typography variant="body2" sx={{ fontWeight: 700 }}>{user.displayName}</Typography>}
          <Typography variant="caption" sx={{ color: '#9eb8d0' }}>{user.email}</Typography>
          <Box sx={{ mt: 0.75 }}>
            {isAdmin
              ? <Chip label="Admin" size="small" sx={{ fontSize: '0.62rem', height: 18, bgcolor: '#3a1a6e', color: '#c084fc', fontWeight: 700 }} />
              : isPremium
              ? <Chip label="Nerds+ Active ⚡" color="secondary" size="small" sx={{ fontSize: '0.62rem', height: 18, fontWeight: 700 }} />
              : <Chip label="Free Account" size="small" sx={{ fontSize: '0.62rem', height: 18, bgcolor: '#1a2e44', color: '#9eb8d0' }} />
            }
          </Box>
        </Box>

        {/* Phase C.1b — compact Career Mode switcher, ESTABLISHED HERE
            rather than as a new top-level Navbar element (per this
            phase's own "do not make this a large permanent navigation
            element" instruction) — reuses this already-existing dropdown
            instead of adding new AppBar chrome. Only rendered once the
            account has 2+ enabled modes ("do not show modes the user has
            not enabled unless the selector is specifically in an Edit
            Modes/settings context" — with only 0-1 modes enabled there is
            nothing to switch between, so this stays hidden and Account
            Settings below is the only way to enable more). Picking a mode
            here only ever calls setActiveMode() (037's set_active_
            career_mode RPC) — it can NEVER enable a mode that isn't
            already enabled, matching AccountSettingsPage.js's own
            behavior exactly. */}
        {careerModes.length > 1 && (
          <>
            <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)' }} />
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.06em' }}>
                MODE
              </Typography>
            </Box>
            {CAREER_MODES.filter(m => careerModes.includes(m)).map(mode => (
              <MenuItem
                key={mode}
                onClick={() => { setAnchor(null); if (mode !== activeCareerMode) setActiveMode(mode); }}
                sx={{ fontSize: '0.85rem', py: 0.75, color: mode === activeCareerMode ? '#2ea84b' : '#c0d4e8', fontWeight: mode === activeCareerMode ? 700 : 400 }}
              >
                {mode === activeCareerMode ? '✓ ' : '   '}{CAREER_MODE_META[mode].emoji} {CAREER_MODE_META[mode].label}
              </MenuItem>
            ))}
          </>
        )}

        <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)' }} />
        <MenuItem onClick={() => { setAnchor(null); navigate('/account'); }}
          sx={{ color: '#9eb8d0', fontSize: '0.85rem', py: 1.25 }}>
          ⚙️ Account Settings
        </MenuItem>
        {isFree && (
          <MenuItem onClick={() => { setAnchor(null); setUpgradeModalOpen(true); }}
            sx={{ color: '#f5a623', fontWeight: 700, fontSize: '0.85rem', py: 1.25 }}>
            ⚡ Upgrade to Nerds+ — $6/mo
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={() => { setAnchor(null); navigate('/admin'); }}
            sx={{ color: '#c084fc', fontWeight: 600, fontSize: '0.85rem', py: 1.25 }}>
            🔧 Admin Panel
          </MenuItem>
        )}
        <MenuItem onClick={() => { setAnchor(null); setAuthModalOpen(true); }}
          sx={{ color: '#9eb8d0', fontSize: '0.85rem', py: 1.25 }}>
          🔑 Change Password
        </MenuItem>
        <MenuItem onClick={handleSignOut}
          sx={{ color: '#9eb8d0', fontSize: '0.85rem', py: 1.25, '&:hover': { color: '#f44336' } }}>
          Sign Out
        </MenuItem>
      </Menu>
    </>
  );
}

export default function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const theme     = useTheme();
  const isMobile  = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  // currentUser = real Firebase auth (never simulated)
  // isFree/isPremium/isAdmin = sim-aware (follow viewAsTier when admin is testing)
  const { currentUser, isPremium, isFree, isAdmin, setAuthModalOpen, setUpgradeModalOpen } = useAuth();

  const handleNav = (path) => { setDrawerOpen(false); navigate(path); };
  const isActive  = (path) => location.pathname === path || (path === '/nfl' && location.pathname.startsWith('/nfl') && !location.pathname.startsWith('/nfl-')) || (path === '/college' && location.pathname.startsWith('/college')) || (path === '/fantasy' && location.pathname.startsWith('/fantasy'));

  return (
    <>
      <AppBar position="sticky" sx={{
        backgroundColor: 'rgba(5,12,22,0.94)',
        borderBottom: '1px solid rgba(26,46,68,0.7)',
        zIndex: 1200,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: '0 1px 0 rgba(46,168,75,0.08)',
      }}>
        <Toolbar sx={{ gap: 0.5, minHeight: '56px !important' }}>

          {/* Logo */}
          <Typography variant="h6" sx={{ fontWeight: 900, cursor: 'pointer', mr: 2, whiteSpace: 'nowrap', fontSize: '1rem', letterSpacing: '-0.02em' }}
            onClick={() => navigate('/')}>
            🏈{' '}
            <span style={{
              background: 'linear-gradient(135deg, #2ea84b 0%, #3ec45e 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>Football</span>{' '}
            <span style={{ color: '#e8f0f8' }}>for Nerds</span>
          </Typography>

          {!isMobile && (
            <>
              {NAV_ITEMS.map(item => (
                <Button key={item.label} onClick={() => handleNav(item.path)}
                  sx={{
                    color: isActive(item.path) ? '#2ea84b' : '#8aaac8',
                    fontWeight: isActive(item.path) ? 700 : 500,
                    textTransform: 'none', px: 1.4, minWidth: 'auto', fontSize: '0.85rem',
                    letterSpacing: '-0.01em',
                    borderBottom: isActive(item.path) ? '2px solid #2ea84b' : '2px solid transparent',
                    borderRadius: 0, pb: 0,
                    transition: 'color 0.15s',
                    '&:hover': { color: '#c8dff0', backgroundColor: 'transparent' },
                    background: 'none !important',
                  }}>
                  {item.label}
                </Button>
              ))}

              <Box sx={{ flex: 1 }} />

              {/* Support label */}
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#f5a623', mr: 0.5, whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                Help Support This Project →
              </Typography>

              {/* Ko-fi button */}
              <Tooltip title="Support the project on Ko-fi">
                <IconButton component="a" href="https://ko-fi.com/footballfornerds" target="_blank" rel="noopener noreferrer"
                  size="small" sx={{ color: '#ff5e5b', mr: 0.5, '&:hover': { backgroundColor: 'rgba(255,94,91,0.1)' } }}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>☕</Typography>
                </IconButton>
              </Tooltip>

              {/* Stripe donation button */}
              <Tooltip title="One-time donation via Stripe">
                <IconButton component="a" href="https://donate.stripe.com/footballfornerds" target="_blank" rel="noopener noreferrer"
                  size="small" sx={{ color: '#635bff', mr: 0.5, '&:hover': { backgroundColor: 'rgba(99,91,255,0.1)' } }}>
                  <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>💜</Typography>
                </IconButton>
              </Tooltip>

              {/* Connect label + social icons */}
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#9eb8d0', mr: 0.5, whiteSpace: 'nowrap', display: { xs: 'none', md: 'block' } }}>
                Connect →
              </Typography>
              <Tooltip title="Follow us on X (Twitter)">
                <IconButton component="a" href="https://x.com/footballfornerds" target="_blank" rel="noopener noreferrer"
                  size="small" sx={{ color: '#e7e7e7', mr: 0.5, '&:hover': { backgroundColor: 'rgba(231,231,231,0.1)' } }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, lineHeight: 1, fontFamily: 'monospace' }}>𝕏</Typography>
                </IconButton>
              </Tooltip>
              <Tooltip title="Follow us on Instagram">
                <IconButton component="a" href="https://instagram.com/footballfornerds" target="_blank" rel="noopener noreferrer"
                  size="small" sx={{ color: '#e1306c', mr: 0.75, '&:hover': { backgroundColor: 'rgba(225,48,108,0.1)' } }}>
                  <InstagramIcon size={20} />
                </IconButton>
              </Tooltip>

              {/* Discord button */}
              <Tooltip title={isPremium || isAdmin ? 'Discord Community' : 'Nerd+ Access Required'}>
                <span>
                  <IconButton
                    component={isPremium || isAdmin ? 'a' : 'button'}
                    href={isPremium || isAdmin ? 'https://discord.gg/footballfornerds' : undefined}
                    target={isPremium || isAdmin ? '_blank' : undefined}
                    rel={isPremium || isAdmin ? 'noopener noreferrer' : undefined}
                    disabled={!isPremium && !isAdmin}
                    size="small"
                    sx={{
                      color: isPremium || isAdmin ? '#5865F2' : '#475569',
                      mr: 0.5,
                      opacity: isPremium || isAdmin ? 1 : 0.5,
                      '&:hover': { backgroundColor: isPremium || isAdmin ? 'rgba(88,101,242,0.1)' : 'transparent' },
                    }}
                  >
                    <DiscordIcon size={20} />
                  </IconButton>
                </span>
              </Tooltip>

              {/* Right side — auth state uses real currentUser, upgrade CTA uses simulated tier */}
              {!currentUser ? (
                // Not logged in (real state)
                <>
                  <Button onClick={() => setAuthModalOpen(true)}
                    variant="contained" color="primary" size="small"
                    sx={{ textTransform: 'none', fontWeight: 700, mr: 1, fontSize: '0.82rem' }}>
                    Sign Up Free
                  </Button>
                  <Button onClick={() => setAuthModalOpen(true)}
                    variant="text" size="small"
                    sx={{ color: '#9eb8d0', textTransform: 'none', fontSize: '0.82rem' }}>
                    Sign In
                  </Button>
                </>
              ) : (
                // Logged in (real state)
                <>
                  {isFree && (
                    <Button onClick={() => setUpgradeModalOpen(true)} size="small"
                      sx={{
                        color: '#f5a623', fontWeight: 700, textTransform: 'none',
                        border: '1px solid #f5a62344', borderRadius: 2,
                        px: 1.5, mr: 1, fontSize: '0.82rem',
                        '&:hover': { backgroundColor: '#f5a62312' },
                      }}>
                      ⚡ Nerds+ — $6/mo
                    </Button>
                  )}
                  <UserMenu user={currentUser} isPremium={isPremium} isFree={isFree} isAdmin={isAdmin} />
                </>
              )}
            </>
          )}

          {isMobile && (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
              {!currentUser && (
                <Button onClick={() => setAuthModalOpen(true)} variant="contained" color="primary" size="small"
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                  Sign Up Free
                </Button>
              )}
              {currentUser && <UserMenu user={currentUser} isPremium={isPremium} isFree={isFree} isAdmin={isAdmin} />}
              <IconButton color="inherit" onClick={() => setDrawerOpen(true)} size="small">☰</IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260, backgroundColor: 'rgba(8,18,32,0.99)', height: '100%', p: 2 }}>
          <List>
            {NAV_ITEMS.map(item => (
              <ListItemButton key={item.label} onClick={() => handleNav(item.path)}
                selected={isActive(item.path)}
                sx={{ borderRadius: 1, mb: 0.5, '&.Mui-selected': { bgcolor: 'rgba(46,168,75,0.12)' } }}>
                <ListItemText primary={item.label}
                  primaryTypographyProps={{ sx: { color: isActive(item.path) ? '#2ea84b' : '#e8f0f8', fontWeight: isActive(item.path) ? 700 : 400 } }} />
              </ListItemButton>
            ))}

            <Divider sx={{ borderColor: 'rgba(245,166,35,0.25)', my: 1 }} />

            {!currentUser && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
                <Button fullWidth variant="contained" color="primary"
                  onClick={() => { setDrawerOpen(false); setAuthModalOpen(true); }}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Sign Up Free
                </Button>
                <Button fullWidth variant="outlined" color="primary"
                  onClick={() => { setDrawerOpen(false); setAuthModalOpen(true); }}
                  sx={{ textTransform: 'none' }}>
                  Sign In
                </Button>
              </Box>
            )}

            {isFree && (
              <ListItemButton onClick={() => { setDrawerOpen(false); setUpgradeModalOpen(true); }}
                sx={{ borderRadius: 1 }}>
                <ListItemText primary="⚡ Upgrade to Nerds+ — $6/mo"
                  primaryTypographyProps={{ sx: { color: '#f5a623', fontWeight: 700 } }} />
              </ListItemButton>
            )}

            {isAdmin && (
              <ListItemButton onClick={() => handleNav('/admin')} sx={{ borderRadius: 1 }}>
                <ListItemText primary="🔧 Admin Panel"
                  primaryTypographyProps={{ sx: { color: '#c084fc', fontWeight: 600 } }} />
              </ListItemButton>
            )}

            {currentUser && (
              <Box sx={{ px: 1, pt: 1 }}>
                <Button fullWidth variant="outlined" color="error"
                  onClick={async () => { setDrawerOpen(false); await supabase.auth.signOut(); }}
                  sx={{ textTransform: 'none' }}>
                  Sign Out
                </Button>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
